// Tenant announcement screen — the 4th tab of the monitoring console.
//
// A wrapper on purpose: everything except the write guard is shared with the
// platform and partner tabs through `AnnouncementsManager`.

import { useTranslation } from 'react-i18next';
import { useAdminAppContext } from '../context/AdminAppContext.js';
import type { AdminHttpClient } from '../http.js';
import type { ToastMessage } from '../types.js';
import { AnnouncementsManager } from './announcements/AnnouncementsManager.js';
import {
  canWriteTenantAnnouncements,
  effectiveTenantRoles,
} from './announcements/announcement-rules.js';

export function AnnouncementsScreen({
  http,
  activeTenantId,
  onToast,
}: {
  http: AdminHttpClient;
  /** The act-as tenant. The client and this component both survive a tenant
   *  switch (the token is read per call), so it has to drive a refetch. */
  activeTenantId?: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  // Read the roles from the shared app context instead of widening
  // ScreenContext: a platform/partner admin's BASE role carries no tenant
  // permission, so the write guard must use the act-as role set the server
  // issued.
  const { session } = useAdminAppContext();
  const current = session.session;
  const roles = effectiveTenantRoles({
    baseRoles: current?.bootstrap.membership.roles ?? [],
    actAsRoles: current?.tenantContext?.roles,
  });

  return (
    <>
      <p className="screen-help">{t('screenHelp.announcements')}</p>
      <AnnouncementsManager
        http={http}
        scope="tenant"
        canWrite={canWriteTenantAnnouncements(roles)}
        onToast={onToast}
        refetchKey={activeTenantId}
      />
    </>
  );
}
